import React, { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import { copyFor } from 'config';
import { ErrorPage } from 'components/errors';
import { useSessionContext } from 'contexts/session';
import { LoadingPage } from 'components/loading';
import {
  useSolarState,
  AVAILABLE,
  CANCELED,
  ENROLLED,
  INELIGIBLE,
  PENDING,
  UNAVAILABLE,
  WAITLIST,
} from 'hooks/use-solar-state.hook';
import { usePlanLocation } from 'hooks/use-plan-location.hook';
import { useUser } from 'hooks/use-user.hook';
import { Page } from 'styles';
import { WelcomeModal } from 'components/modals/welcome';
import { Available } from './available';
import { Canceled } from './canceled';
import { Enrolled } from './enrolled';
import { Ineligible } from './ineligible';
import { Pending } from './pending';
import { Unavailable } from './unavailable';
import { Waitlist } from './waitlist';
import {
  GET_STATE_DATA,
  HAS_ADVOCACY_SUBMISSION,
  GET_PLAN_BILLING_INFO,
} from './active.api';

const getCopy = copyFor('solar');

const stateViewMap = {
  [AVAILABLE]: Available,
  [CANCELED]: Canceled,
  [ENROLLED]: Enrolled,
  [INELIGIBLE]: Ineligible,
  [PENDING]: Pending,
  [UNAVAILABLE]: Unavailable,
  [WAITLIST]: Waitlist,
};

export const Active = () => {
  const { userId } = useSessionContext();
  const { planId } = useParams<{ planId?: string }>();
  const state = useSolarState({ planId: Number(planId) });
  const user = useUser();
  const location = usePlanLocation({ planId: Number(planId) });
  const stateData = useQuery<GetStateDataQuery, GetStateDataQueryVariables>(
    GET_STATE_DATA,
    {
      skip: !planId,
      variables: { planId: Number(planId) || -1 },
    }
  );
  const utilityAccount = stateData.data?.plan.utilityAccounts[0];
  const advocacyCampaign = utilityAccount?.utility.state.activeAdvocacyCampaign;
  const submissionData = useQuery<
    HasAdvocacyCampaignSubmissionQuery,
    HasAdvocacyCampaignSubmissionQueryVariables
  >(HAS_ADVOCACY_SUBMISSION, {
    skip: !advocacyCampaign,
    variables: { advocacyCampaignId: advocacyCampaign?.id || -1, userId },
  });
  const planData = useQuery<
    GetPlanBillingInfoQuery,
    GetPlanBillingInfoQueryVariables
  >(GET_PLAN_BILLING_INFO, {
    skip: !planId,
    variables: { id: Number(planId) || -1 },
  });

  useEffect(() => {
    state.vnmRefetch();
    state.subscriptionStatusRefetch();
  }, [stateData.data]);

  const results = [state, user, location, stateData, submissionData, planData];
  if (results.some(result => result.loading))
    return <LoadingPage dataTestId="solar-active-loading" />; // TODO: update loading layout once sunrise is complete
  if (results.some(result => result.error) || !user.data || !stateData.data)
    return <ErrorPage />;

  const View = stateViewMap[state.data.state];
  const { firstName } = user.data;
  const { utility } = stateData.data.plan.utilityAccounts[0];
  const hasAdvocacyCampaignSubmission =
    submissionData?.data?.hasAdvocacyCampaignSubmission;
  const zipCode = utilityAccount?.serviceAddressZip || user.data?.zipCode?.zip;
  const originationSourceType =
    user.data?.originationSource?.originationSourceType;

  return (
    <Page small={{ padding: '0 0 24px' }}>
      <WelcomeModal />
      <View
        {...location.data}
        advocacyCampaign={advocacyCampaign}
        completedAdvocacyCampaign={
          utility.state.mostRecentCompletedAdvocacyCampaign
        }
        firstName={firstName}
        pageHeading={getCopy('heading')}
        stateId={utility.state.id}
        vnmSolarProjectsStatistics={utility.state.vnmSolarProjectsStatistics}
        hasAdvocacyCampaignSubmission={hasAdvocacyCampaignSubmission}
        utilityAccountId={utilityAccount?.id}
        zipCode={zipCode}
        originationSourceType={originationSourceType}
      />
    </Page>
  );
};
